import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardDrive } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { FormFooter, PageHeader, Row, Section, Select } from '../components/form';
import { Alert, Badge, Button, Card, Input } from '../components/ui';
import { api, type Extension } from '../lib/api';

interface RecordingStatus {
  files: number;
  usedBytes: number;
  diskFreeBytes: number | null;
  diskTotalBytes: number | null;
  paused: boolean;
  retentionDays: number;
  minFreeGb: number;
  extensions: Record<string, number>;
}

const MODES = [
  { value: 'never', label: 'Do not record' },
  { value: 'always', label: 'Record every call' },
  { value: 'on_demand', label: 'On demand' },
] as const;

function bytes(value: number | null): string {
  if (value === null) return '—';
  if (value < 1e9) return `${Math.round(value / 1e6)} MB`;
  return `${(value / 1e9).toFixed(1)} GB`;
}

/**
 * Recording: who is recorded, where it is stored, and when it stops.
 *
 * One screen because "why is there no recording" has three answers — it was never enabled for
 * that extension, retention deleted it, or the disk guard paused recording — and an admin should
 * not have to visit three screens to rule them out.
 */
export function Recording() {
  const queryClient = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ['recording-status'],
    queryFn: () => api.get<RecordingStatus>('/api/v1/recording-status'),
    refetchInterval: 30_000,
  });

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  const setAll = useMutation({
    mutationFn: (recordingMode: string) =>
      api.put<{ recordingMode: string; extensions: number }>('/api/v1/extensions/recording-mode', {
        recordingMode,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['extensions'] });
      void queryClient.invalidateQueries({ queryKey: ['recording-status'] });
    },
  });

  const setMode = useMutation({
    mutationFn: ({ id, recordingMode }: { id: string; recordingMode: string }) =>
      api.patch(`/api/v1/extensions/${id}`, { recordingMode }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['extensions'] });
      void queryClient.invalidateQueries({ queryKey: ['recording-status'] });
    },
  });

  const columns: Array<Column<Extension>> = [
    {
      key: 'number',
      header: 'Extension',
      sort: (a, b) => a.number.localeCompare(b.number),
      render: (e) => <span className="font-mono font-medium">{e.number}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (e) => e.name,
    },
    {
      key: 'mode',
      header: 'Recording',
      render: (e) => (
        <div className="w-52">
          <Select
            value={(e as Extension & { recordingMode?: string }).recordingMode ?? 'never'}
            onChange={(recordingMode) => setMode.mutate({ id: e.id, recordingMode })}
          >
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
        </div>
      ),
    },
  ];

  const usedPercent =
    status?.diskTotalBytes && status.diskTotalBytes > 0
      ? Math.round((status.usedBytes / status.diskTotalBytes) * 100)
      : 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Recording"
        description="Which calls are recorded, where the audio lives, and when it is removed."
      />

      {status?.paused && (
        <Alert>
          Recording is paused because free disk space fell below {status.minFreeGb} GB. It resumes
          automatically once space is available — a full disk stops calls entirely, so recording is
          what gets sacrificed first.
        </Alert>
      )}

      <Section
        title="Record everyone"
        description="Applies one policy to every extension at once, instead of setting them one by one."
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={setAll.isPending}
            onClick={() => {
              // Confirmed rather than instant. In most jurisdictions recording a call without
              // telling the parties is unlawful, and this switches it on for the whole
              // organisation — the one click should be the one after thinking, not before.
              if (
                confirm(
                  `Record every call on all ${extensions.length} extensions?\n\n` +
                    'Check what your local law requires you to disclose to callers.',
                )
              ) {
                setAll.mutate('always');
              }
            }}
          >
            {setAll.isPending ? 'Applying…' : 'Record all calls'}
          </Button>

          <Button
            variant="secondary"
            disabled={setAll.isPending}
            onClick={() => {
              if (confirm(`Stop recording on all ${extensions.length} extensions?`)) {
                setAll.mutate('never');
              }
            }}
          >
            Turn recording off everywhere
          </Button>

          {setAll.isSuccess && (
            <span className="text-sm text-muted-foreground">
              Applied to {setAll.data?.extensions ?? 0} extension(s).
            </span>
          )}
        </div>

        {setAll.error ? <Alert className="mt-3">{(setAll.error as Error).message}</Alert> : null}

        {/*
          Stated because it is the question that follows immediately, and the honest answer is
          not "instantly": the engine decides from a cached directory, which this invalidates.
        */}
        <p className="mt-3 text-xs text-muted-foreground">
          Takes effect on the next call. Calls already in progress are not affected.
        </p>
      </Section>

      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <p className="text-sm text-muted-foreground">Recordings stored</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{status?.files ?? 0}</p>
        </Card>
        <Card>
          <p className="text-sm text-muted-foreground">Space used</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {bytes(status?.usedBytes ?? 0)}
          </p>
          <p className="text-xs text-muted-foreground">{usedPercent}% of the disk</p>
        </Card>
        <Card>
          <p className="text-sm text-muted-foreground">Disk free</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {bytes(status?.diskFreeBytes ?? null)}
          </p>
          <p className="text-xs text-muted-foreground">
            of {bytes(status?.diskTotalBytes ?? null)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-muted-foreground">Status</p>
          <p className="mt-1">
            {status?.paused ? (
              <Badge tone="destructive">Paused, low disk</Badge>
            ) : (
              <Badge tone="success">Recording</Badge>
            )}
          </p>
        </Card>
      </div>

      <StorageForm retentionDays={status?.retentionDays ?? 90} minFreeGb={status?.minFreeGb ?? 2} />

      <Section
        title="Per extension"
        description="On demand means the user starts it from their phone or the SDK."
      >
        <DataTable
          rows={extensions}
          columns={columns}
          rowKey={(e) => e.id}
          search={(e) => `${e.number} ${e.name}`}
          searchPlaceholder="Search extensions"
          empty="No extensions."
        />
      </Section>
    </div>
  );
}

function StorageForm({ retentionDays, minFreeGb }: { retentionDays: number; minFreeGb: number }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ retentionDays, minFreeGb });
  const [saved, setSaved] = useState(false);

  // Kept in step when the poll brings fresh values, so the form does not fight the server.
  useEffect(() => setForm({ retentionDays, minFreeGb }), [retentionDays, minFreeGb]);

  const save = useMutation({
    mutationFn: () => api.put('/api/v1/settings/recording', form),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ['recording-status'] });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Section
        title="Storage limits"
        description="Recordings fill a disk faster than anything else on a PBX."
      >
        {saved && <Alert tone="warning">Saved. The worker applies this on its next pass.</Alert>}

        <div className="mt-2 grid gap-4 sm:grid-cols-2">
          <Row
            label="Keep recordings for (days)"
            required
            hint="Older recordings are deleted automatically, audio and database row together."
          >
            <Input
              value={String(form.retentionDays)}
              onChange={(e) =>
                setForm((f) => ({ ...f, retentionDays: Number(e.target.value) || 0 }))
              }
              inputMode="numeric"
            />
          </Row>

          <Row
            label="Stop recording below (GB free)"
            required
            hint="A full disk stops calls, Asterisk's spool and the database — so recording stops first."
          >
            <Input
              value={String(form.minFreeGb)}
              onChange={(e) => setForm((f) => ({ ...f, minFreeGb: Number(e.target.value) || 0 }))}
              inputMode="numeric"
            />
          </Row>
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-[--radius] border border-border p-3 text-sm text-muted-foreground">
          <HardDrive size={16} className="mt-0.5 shrink-0" />
          <span>
            Roughly 0.2 MB per minute of recorded audio after conversion to Opus, so an hour of
            calls is about 12 MB. On a small VPS, 90 days of a busy team is worth planning for.
          </span>
        </div>
      </Section>

      <FormFooter saving={save.isPending} />
    </form>
  );
}
